import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Modal } from '../components/ui';
import { UserAvatar } from '../components/UserAvatar';
import { ApprovalStatusBadge } from '../components/ApprovalWorkflow';
import {
  ApprovalRequest,
  ApprovalStatus,
  listApprovals,
  decideApproval,
} from '../lib/approvalsApi';
import { useAuth } from '../contexts/AuthContext';
import { useIsApprover } from '../hooks/usePermission';
import './Approvals.css';
import { Trans, useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface ApprovalsProps {
  apiUrl: string;
}

const FILTERS: { key: ApprovalStatus | 'all'; labelKey: string }[] = [
  { key: 'pending', labelKey: 'pages.approvals.pending' },
  { key: 'approved', labelKey: 'pages.approvals.approved' },
  { key: 'rejected', labelKey: 'pages.approvals.rejected' },
  { key: 'all', labelKey: 'pages.approvals.all' },
];

const ACTION_ICON: Record<string, any> = {
  'deploy-agent': 'rocket',
  'modify-policy': 'shield',
  'export-dlp': 'download',
};

const Approvals: React.FC<ApprovalsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { userAttributes } = useAuth();
  const isApprover = useIsApprover();
  const [filter, setFilter] = useState<ApprovalStatus | 'all'>('pending');
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<{ req: ApprovalRequest; kind: 'approved' | 'rejected' } | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await listApprovals(apiUrl, {
        status: filter === 'all' ? undefined : filter,
        // Non-approvers only see their own requests
        mine: !isApprover,
      });
      setApprovals(items);
    } catch (e: any) {
      setError(e.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, filter, isApprover]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const submitDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      await decideApproval(apiUrl, decision.req.approvalId, decision.kind, note.trim() || undefined);
      setDecision(null);
      setNote('');
      fetchApprovals();
    } catch (e: any) {
      setError(e.message || 'Failed to submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = approvals.filter((a) => a.status === 'pending').length;

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="checkBadge" size={20} /> {t('pages.approvals.approvals')}
          </h1>
          <p className="subtitle">
            {isApprover
              ? t('pages.approvals.reviewAndDecideSensitiveOperationsAwaiting')
              : t('pages.approvals.trackTheStatusOfYourApproval')}
          </p>
        </div>
        <button className="ent-btn" onClick={fetchApprovals}>
          <Icon name="refresh" /> {t('pages.approvals.refresh')}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="apr-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`apr-filter ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {t(f.labelKey)}
            {f.key === 'pending' && pendingCount > 0 && filter === 'pending' && (
              <span className="apr-filter-count">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="apr-inline-error">
          <Icon name="warning" size={15} /> {error}
        </div>
      )}

      {loading ? (
        <div className="apr-empty">{t('pages.approvals.loadingApprovals')}</div>
      ) : error ? null : approvals.length === 0 ? (
        <div className="apr-empty">
          <Icon name="checkBadge" size={40} />
          <h3>{t('pages.approvals.nothingHere')}</h3>
          <p>
            {filter === 'pending'
              ? t('pages.approvals.thereAreNoRequestsAwaitingA')
              : `No ${filter === 'all' ? '' : filter} requests to show.`}
          </p>
        </div>
      ) : (
        <div className="apr-list">
          {approvals.map((a) => (
            <div key={a.approvalId} className="apr-card">
              <div className={`apr-card-icon apr-icon-${a.actionType}`}>
                <Icon name={ACTION_ICON[a.actionType] || 'bell'} size={20} />
              </div>

              <div className="apr-card-main">
                <div className="apr-card-head">
                  <span className="apr-card-action">{a.actionLabel}</span>
                  {a.resourceLabel && <span className="apr-card-resource">{a.resourceLabel}</span>}
                  <ApprovalStatusBadge status={a.status} />
                </div>
                {a.reason && <p className="apr-card-reason">“{a.reason}”</p>}
                <div className="apr-card-meta">
                  <span className="apr-card-requester">
                    <UserAvatar displayName={a.requestedByName || a.requestedBy} size="xs" />
                    {a.requestedByName || a.requestedBy}
                  </span>
                  <span className="apr-card-time">{parseServerDate(a.requestedAt).toLocaleString()}</span>
                  {a.status !== 'pending' && a.decidedByName && (
                    <span className="apr-card-decider">
                      <Trans
                        i18nKey={a.status === 'approved' ? 'pages.approvals.approvedBy' : 'pages.approvals.rejectedBy'}
                        values={{ name: a.decidedByName }}
                        components={{ b: <strong /> }}
                      />
                    </span>
                  )}
                </div>
                {a.decisionNote && <p className="apr-card-decision-note">{t('pages.approvals.note', { v0: a.decisionNote })}</p>}
              </div>

              {/* Approvers can act on pending requests */}
              {isApprover && a.status === 'pending' && (
                <div className="apr-card-actions">
                  <button
                    className="ent-btn danger sm"
                    onClick={() => { setDecision({ req: a, kind: 'rejected' }); setNote(''); }}
                  >
                    <Icon name="deny" size={13} /> {t('pages.approvals.reject')}
                  </button>
                  <button
                    className="ent-btn primary sm"
                    onClick={() => { setDecision({ req: a, kind: 'approved' }); setNote(''); }}
                  >
                    <Icon name="checkCircle" size={13} /> {t('pages.approvals.approve')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Decision modal */}
      {decision && (
        <Modal
          title={decision.kind === 'approved' ? t('pages.approvals.approveRequest') : t('pages.approvals.rejectRequest')}
          icon={decision.kind === 'approved' ? 'checkCircle' : 'deny'}
          onClose={() => !submitting && setDecision(null)}
          maxWidth={460}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setDecision(null)} disabled={submitting}>
                {t('pages.approvals.cancel')}
              </button>
              <button
                className={`ent-btn ${decision.kind === 'approved' ? 'primary' : 'danger'}`}
                onClick={submitDecision}
                disabled={submitting}
              >
                <Icon name={decision.kind === 'approved' ? 'checkCircle' : 'deny'} />
                {submitting ? t('pages.approvals.submitting') : decision.kind === 'approved' ? t('pages.approvals.approve') : t('pages.approvals.reject')}
              </button>
            </>
          }
        >
          <p className="apr-decision-summary">
            <strong>{decision.req.actionLabel}</strong>
            {decision.req.resourceLabel && <> — {decision.req.resourceLabel}</>}
            <br />
            <Trans i18nKey="pages.approvals.requestedBy" values={{ name: decision.req.requestedByName || decision.req.requestedBy }} />
          </p>
          <div className="ent-field">
            <label>
              {t('pages.approvals.decisionNote')} {decision.kind === 'rejected' ? t('pages.approvals.recommended') : t('pages.approvals.optional')}
            </label>
            <textarea
              className="ent-textarea"
              rows={3}
              placeholder={decision.kind === 'approved' ? t('pages.approvals.addContextForTheAuditTrail') : t('pages.approvals.explainWhyThisWasRejected')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Approvals;
